import type {Ref} from 'vue';
import {computed} from 'vue';
import {dateAfter, email, isFilled, matchRegex, required, requiredIf, withMessage, withTooltip} from '@regle/rules';
import {useTranslation} from '#imports';
import {useRegle, validateForm} from '@malt/forms';
import type {ClientCompanyBillingInfoParam, ProjectInformationResource, ProjectValidationParam} from '~/openapi';

export function useClientCompanyBillingInfoValidationForm(
    clientCompanyBillingInfoValidationData: Ref<ClientCompanyBillingInfoParam & ProjectValidationParam>,
    projectInformation: Ref<ProjectInformationResource>,
) {
    const {t} = useTranslation();

    const formId = 'client-company-billing-validation-form';

    const isDateRequired = computed(() => projectInformation.value.projectPeriodRequired);

    const {r$} = useRegle(clientCompanyBillingInfoValidationData, {
        billingContact: {email, required},
        periodStartDate: {required: requiredIf(isDateRequired)},
        periodEndDate: {
            required: requiredIf(isDateRequired),
            dateAfter: dateAfter(() => clientCompanyBillingInfoValidationData.value.periodStartDate),
        },
        purchaseOrderNumber: {
            required: withMessage(
                requiredIf(() => projectInformation.value.lot.purchaseOrderMandatory),
                () => t('projectclient.billinginformation.invoice.purchaseorder.required'),
            ),
            numberFormat: withTooltip(
                () => true,
                ({$value}) => {
                    if (isFilled($value)) {
                        if (!matchRegex($value as string, /^(?=.*\d)[\w-]{1,25}$/)) {
                            return t('projectclient.billinginformation.invoice.purchaseorder.regex');
                        }
                        return [];
                    }
                    return [];
                },
            ),
        },
    });

    async function triggerValidation(): Promise<boolean> {
        const {valid} = await validateForm(r$, {formId});
        return valid;
    }

    return {
        r$,
        formId,
        validateForm: triggerValidation,
    };
}
